/**
 * /forgot-password — starts a password reset.
 *
 * The auth service answers with the same message whether or not the address
 * has an account, so this page shows that message either way. A visitor must
 * not be able to learn which addresses are registered.
 */

import { useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@/features/auth/authContext'
import { ApiError } from '@/lib/api'
import { FridayMark } from '@/components/branding/FridayMark'
import { useTheme } from '@/features/theme/ThemeProvider'

export function ForgotPasswordPage() {
  const { forgotPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const { theme } = useTheme()

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await forgotPassword(email)
      setSent(true)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not start the password reset. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-app px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <FridayMark size={64} className="mx-auto mb-3" />
          <h1 className="text-2xl font-semibold tracking-tight">Reset your password</h1>
          <p className="mt-1 text-sm text-fg-muted">
            {sent ? 'We have sent you a reset link.' : 'Tell us the address on your account.'}
          </p>
        </div>

        {sent ? (
          <div
            className="space-y-4 border border-border bg-elevated p-6 shadow-lg"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <p className="text-sm text-fg-muted">
              If an account exists for <span className="font-medium text-fg">{email}</span>, a reset link is on its
              way. The link works for one hour.
            </p>
            <Link to="/login" search={{ next: undefined }} className="btn-primary w-full justify-center">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="space-y-3 border border-border bg-elevated p-6 shadow-lg"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <label className="block">
              <span className="text-xs font-medium text-fg">Email</span>
              <input
                type="email"
                autoComplete="email"
                required
                className="input mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                placeholder="you@example.com"
              />
            </label>
            {error && (
              <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
                {error}
              </div>
            )}
            <button type="submit" className="btn-primary w-full justify-center" disabled={busy}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}

        <p className="mt-4 text-center text-xs text-fg-muted">
          <Link to="/login" search={{ next: undefined }} className="font-medium text-accent hover:underline">
            Back to sign in
          </Link>
          <br />
          <span className="opacity-60">Theme · {theme.name}</span>
        </p>
      </div>
    </div>
  )
}
